import React, { Fragment, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useListStockMovementsQuery } from '../../../redux/api/stockApi';

const fmt = (iso) => new Date(iso).toLocaleString('de-DE');

// The model's type_display is English; show German labels in the UI.
const TYPE_LABELS = {
  IN: 'Wareneingang',
  OUT: 'Warenausgang',
  TRANSFER: 'Umlagerung',
  DEL_TRANS: 'Lieferung angelegt',
  DEL_CONF: 'Lieferung bestätigt',
};

const typeLabel = (m) => TYPE_LABELS[m.type] || m.type_display || m.type;

const ListStockMovements = () => {
  const curEvent = useSelector((state) => state.auth.currentEvent);
  const { data: movements, isLoading } = useListStockMovementsQuery(
    { eventId: curEvent?.id },
    { skip: !curEvent?.id }
  );
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return movements ?? [];
    return (movements ?? []).filter((m) =>
      [
        m.article_name,
        m.from_storage_name,
        m.to_storage_name,
        typeLabel(m),
        m.text,
        m.created_by_username,
      ]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(term))
    );
  }, [movements, searchTerm]);

  if (!curEvent?.id) {
    return <p className='mt-4'>Bitte zuerst ein Event auswählen.</p>;
  }

  if (isLoading) {
    return <p className='mt-4'>Lagerbewegungen werden geladen...</p>;
  }

  return (
    <Fragment>
      <h4>Lagerbewegungen</h4>

      <div className='mb-3'>
        <input
          type='text'
          className='form-control'
          placeholder='Suche nach Artikel, Lagerort, Typ, Kommentar, Person...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <table className='table table-striped mt-2 align-middle text-start'>
        <thead>
          <tr>
            <th>Datum</th>
            <th>Typ</th>
            <th>Artikel</th>
            <th>Von</th>
            <th>Nach</th>
            <th className='text-end'>Menge</th>
            <th className='text-end'>Retoure</th>
            <th>Kommentar</th>
            <th>Erfasst von</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((m) => (
            <tr key={m.id}>
              <td>{fmt(m.created_at)}</td>
              <td>{typeLabel(m)}</td>
              <td>{m.article_name}</td>
              <td>{m.from_storage_name || '—'}</td>
              <td>{m.to_storage_name || '—'}</td>
              <td className='text-end'>{m.quantity}</td>
              <td className='text-end'>{m.quantity_returned ?? '—'}</td>
              <td style={{ maxWidth: 260, wordBreak: 'break-word' }}>
                {m.text || '—'}
                {m.related_delivery_id && (
                  <>
                    {' '}
                    <Link
                      to={`/${
                        m.related_delivery_kind === 'bar'
                          ? 'bar-delivery'
                          : 'crew-delivery'
                      }/${m.related_delivery_id}`}
                      title='Zur Lieferung'
                    >
                      ↗
                    </Link>
                  </>
                )}
              </td>
              <td>{m.created_by_username || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {filtered.length === 0 && (
        <p className='text-muted'>
          {searchTerm
            ? `Keine Bewegungen gefunden für "${searchTerm}".`
            : 'Noch keine Lagerbewegungen erfasst.'}
        </p>
      )}
    </Fragment>
  );
};

export default ListStockMovements;
